/**
 * 화면 캡처 위의 번호 안내.
 *
 * 설명서를 여는 사람은 대개 콘솔을 옆에 띄워 두고 "지금 내 화면의 어디" 를 찾는다.
 * 그래서 글보다 **실제 화면 그림에 번호를 얹는 것**이 먼저다. 번호 위에 손을 올리면
 * 설명이, 설명 위에 손을 올리면 그림의 그 자리가 밝아진다 — 눈이 둘 사이를 오가며
 * 헤매지 않게.
 *
 * 그림과 위치는 `shots/` 에 있다. 화면을 고치면 `scripts/manual/capture.mjs` 로 다시 찍는다.
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Maximize2, X } from 'lucide-react';
import type { ScreenMarker } from './types.js';

type Box = readonly [number, number, number, number];

export interface ShotEntry {
  width: number;
  height: number;
  markers: Record<string, Box>;
}

// JSON 을 모듈로 직접 들이지 않고 glob 으로 읽는다 — 타입 설정(resolveJsonModule)을 이 한 파일 때문에 바꾸지 않는다.
const SHOTS = (Object.values(import.meta.glob('./shots/shots.json', { eager: true, import: 'default' }))[0] ??
  {}) as Record<string, ShotEntry>;

const IMAGES = import.meta.glob('./shots/*.webp', { eager: true, query: '?url', import: 'default' }) as Record<
  string,
  string
>;

export function shotUrl(id: string): string | undefined {
  return IMAGES[`./shots/${id}.webp`];
}

export function shotOf(id: string): ShotEntry | undefined {
  return SHOTS[id];
}

/** 번호 동그라미 — 그림 위와 설명 목록에서 같은 모양을 쓴다. */
function Badge({ n, active = false }: { n: number; active?: boolean }): ReactNode {
  return (
    <span
      className={`inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1.5 text-[12px] font-bold leading-none shadow-sm ring-2 ring-white transition-colors ${
        active ? 'bg-th-5 text-[#1a2e05]' : 'bg-th-3 text-white'
      }`}
    >
      {n}
    </span>
  );
}

const boxStyle = ([x, y, w, h]: Box): CSSProperties => ({
  left: `${x}%`,
  top: `${y}%`,
  width: `${w}%`,
  height: `${h}%`,
});

/** 그림 위에 번호 상자를 얹는다. `active` 가 있으면 나머지를 어둡게 해서 그 자리만 드러낸다. */
function AnnotatedImage({
  id,
  markers,
  active,
  onActive,
  showMarkers,
}: {
  id: string;
  markers: readonly ScreenMarker[];
  active: string | null;
  onActive: (key: string | null) => void;
  showMarkers: boolean;
}): ReactNode {
  const shot = SHOTS[id];
  const url = shotUrl(id);
  if (!shot || !url) return null;

  return (
    <div className="relative w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
      <img
        src={url}
        alt=""
        width={shot.width}
        height={shot.height}
        loading="lazy"
        className="block h-auto w-full select-none"
        draggable={false}
      />
      {showMarkers &&
        markers.map((marker, index) => {
          const box = shot.markers[marker.key];
          if (!box) return null;
          const isActive = active === marker.key;
          return (
            <button
              key={marker.key}
              type="button"
              aria-label={`${index + 1}. ${marker.label}`}
              onMouseEnter={() => onActive(marker.key)}
              onMouseLeave={() => onActive(null)}
              onFocus={() => onActive(marker.key)}
              onBlur={() => onActive(null)}
              onClick={() => onActive(isActive ? null : marker.key)}
              className={`absolute rounded-md transition-[box-shadow,border-color] duration-150 ${
                isActive
                  ? 'z-20 border-2 border-th-5 shadow-[0_0_0_9999px_rgba(15,23,42,0.45)]'
                  : 'z-10 border-2 border-th-5/80 hover:border-th-5'
              }`}
              style={boxStyle(box)}
            >
              <span className="absolute -left-2.5 -top-2.5">
                <Badge n={index + 1} active={isActive} />
              </span>
            </button>
          );
        })}
    </div>
  );
}

/** 크게 보기 — 그림이 본문 폭에 줄어 글자가 작을 때. */
function Lightbox({
  id,
  title,
  markers,
  onClose,
}: {
  id: string;
  title: string;
  markers: readonly ScreenMarker[];
  onClose: () => void;
}): ReactNode {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-slate-900/90" role="dialog" aria-modal="true" aria-label={title}>
      <div className="flex items-center gap-3 px-4 py-3 text-white">
        <p className="flex-1 truncate text-[14px] font-semibold">{title}</p>
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-300 hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-auto px-4 pb-6">
        <div className="mx-auto max-w-[1600px]">
          <AnnotatedImage id={id} markers={markers} active={active} onActive={setActive} showMarkers />
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ScreenGuide({
  shot,
  title,
  markers,
  renderText,
}: {
  shot: string;
  title: string;
  markers: readonly ScreenMarker[];
  renderText: (text: string) => ReactNode;
}): ReactNode {
  const [active, setActive] = useState<string | null>(null);
  const [showMarkers, setShowMarkers] = useState(true);
  const [zoomed, setZoomed] = useState(false);

  if (!SHOTS[shot] || !shotUrl(shot)) return null;

  return (
    <figure className="my-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <figcaption className="flex-1 text-[13px] font-semibold text-slate-800">{title}</figcaption>
        <button
          type="button"
          onClick={() => setShowMarkers((value) => !value)}
          className="btn btn-tiny"
        >
          {showMarkers ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {showMarkers ? '번호 숨기기' : '번호 보기'}
        </button>
        <button
          type="button"
          onClick={() => setZoomed(true)}
          className="btn btn-tiny"
        >
          <Maximize2 className="h-3.5 w-3.5" />
          크게 보기
        </button>
      </div>

      <AnnotatedImage id={shot} markers={markers} active={active} onActive={setActive} showMarkers={showMarkers} />

      <ol className="mt-4 grid gap-2 md:grid-cols-2">
        {markers.map((marker, index) => {
          const isActive = active === marker.key;
          return (
            <li
              key={marker.key}
              onMouseEnter={() => setActive(marker.key)}
              onMouseLeave={() => setActive(null)}
              className={`flex gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                isActive ? 'border-th-4 bg-th-2/50' : 'border-slate-200 bg-white'
              }`}
            >
              <Badge n={index + 1} active={isActive} />
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold leading-snug text-slate-900">{marker.label}</p>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-slate-600">{renderText(marker.text)}</p>
              </div>
            </li>
          );
        })}
      </ol>

      {zoomed && <Lightbox id={shot} title={title} markers={markers} onClose={() => setZoomed(false)} />}
    </figure>
  );
}

/**
 * 한 구성 요소를 둘레와 함께 잘라 크게 보인다 — 시나리오의 단계마다 "여기를 누르세요".
 *
 * 둘레를 얼마나 남길지가 요점이다. 단추만 딱 자르면 그것이 화면의 어디인지 모르고,
 * 너무 넓으면 확대한 의미가 없다. 구성 요소 크기의 1.2배쯤을 여백으로 두고, 너무 가늘거나
 * 납작해지지 않게 비율을 묶는다.
 */
export function ShotCrop({ shot, marker }: { shot: string; marker: string }): ReactNode {
  const entry = SHOTS[shot];
  const url = shotUrl(shot);
  const box = entry?.markers[marker];
  if (!entry || !url || !box) return null;

  const [x, y, w, h] = box;
  // 백분율을 픽셀로 옮겨 둘레를 잡는다 — 가로·세로 백분율은 단위가 다르다.
  const px = { x: (x / 100) * entry.width, y: (y / 100) * entry.height, w: (w / 100) * entry.width, h: (h / 100) * entry.height };
  // 둘레 여백은 구성 요소 크기에 비례하되 묶는다 — 큰 구역(패널)을 두 배로 늘리면 화면 전체가 된다.
  const pad = Math.min(Math.max(Math.max(px.w, px.h) * 0.35, 48), 200);
  let cw = Math.max(px.w + pad * 2, 480);
  let ch = Math.max(px.h + pad * 2, 200);
  // 비율을 묶는다: 가로:세로 1:1 ~ 3:1.
  if (cw / ch > 3) ch = cw / 3;
  if (cw / ch < 1) cw = ch;
  cw = Math.min(cw, entry.width);
  ch = Math.min(ch, entry.height);
  const cx = Math.min(Math.max(px.x + px.w / 2 - cw / 2, 0), entry.width - cw);
  const cy = Math.min(Math.max(px.y + px.h / 2 - ch / 2, 0), entry.height - ch);

  const region = { x: (cx / entry.width) * 100, y: (cy / entry.height) * 100, w: (cw / entry.width) * 100, h: (ch / entry.height) * 100 };

  return (
    <div
      className="relative w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
      style={{ aspectRatio: `${cw} / ${ch}` }}
    >
      {/*
        **지연 로딩을 쓰지 않는다**(2026-10-28 사용자 보고 "처음 시작하기 2번 빈화면 캡쳐" 외 7곳). 이 그림은 잘라 보이려고 틀 밖으로
        밀어 둔(`top: -…%`) 절대 위치라, 받기 전에는 높이가 0 인 상자가 틀 밖에 놓인다 — WebKit(사파리·codEdit)은 그 상자가
        화면에 들어오지 않는다고 보고 끝내 받지 않아 칸이 비었다. 같은 그림 파일을 여러 단계가 나눠 써 한 번 받으면 된다.
      */}
      <img
        src={url}
        alt=""
        decoding="async"
        draggable={false}
        className="absolute max-w-none select-none"
        style={{
          width: `${(100 / region.w) * 100}%`,
          left: `${(-region.x / region.w) * 100}%`,
          top: `${(-region.y / region.h) * 100}%`,
        }}
      />
      <span
        className="absolute rounded-md border-2 border-th-5 shadow-[0_0_0_9999px_rgba(15,23,42,0.28)]"
        style={{
          left: `${((x - region.x) / region.w) * 100}%`,
          top: `${((y - region.y) / region.h) * 100}%`,
          width: `${(w / region.w) * 100}%`,
          height: `${(h / region.h) * 100}%`,
        }}
      />
    </div>
  );
}
